import React, { useState } from 'react';
import { FieldType } from '@/features/application/types/forms';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/features/ui/primitives/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/features/ui/components/Select';
import { Button } from '@/features/ui/primitives/button';
import { AlertTriangle } from 'lucide-react';

interface TypeChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newType: FieldType) => void;
  compatibleTypes: FieldType[];
  currentFormType: FieldType;
  crmFieldType: string;
  crmFieldLabel: string;
}

// Purpose: Modal component for confirming form field type conversions during CRM mapping
export const TypeChangeModal: React.FC<TypeChangeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  compatibleTypes,
  currentFormType,
  crmFieldType,
  crmFieldLabel,
}) => {
  const [selectedType, setSelectedType] = useState<FieldType | undefined>(undefined);

  const handleConfirm = () => {
    if (selectedType) {
      onConfirm(selectedType);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedType(undefined);
    onClose();
  };

  const handleSelectChange = (value: string) => {
    const validType = compatibleTypes.find((type) => type === value);
    setSelectedType(validType);
  };

  // Format field type for display
  const formatFieldType = (type: FieldType): string => {
    return type.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-accent" />
            Field Type Conversion Required
          </DialogTitle>
          <DialogDescription className="text-left text-slate-700">
            You're mapping a <strong>{formatFieldType(currentFormType)}</strong> field to the CRM
            field <strong>"{crmFieldLabel}"</strong> which is a <strong>{crmFieldType}</strong>{' '}
            type.
            <br />
            <br />
            <strong className="text-accent">
              This will change how the field appears and behaves on your form.
            </strong>
            <br />
            <br />
            Please select which form field type you'd like to convert to:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Convert form field to:</label>
            <Select value={selectedType || ''} onValueChange={handleSelectChange}>
              <SelectTrigger className="border-slate-200 focus:border-brand-300 focus:ring-brand-200">
                <SelectValue placeholder="Select a compatible field type..." />
              </SelectTrigger>
              <SelectContent>
                {compatibleTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {formatFieldType(type)}
                    {type === currentFormType && ' (current)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedType && (
            <div className="p-3 bg-info-50 border border-info-200 rounded-md">
              <p className="text-sm text-info-700">
                The form field will be converted to a{' '}
                <strong>{formatFieldType(selectedType)}</strong> field and mapped to the CRM field.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-slate-200 text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedType}
            className="bg-accent text-accent-foreground hover:bg-accent/90 disabled:bg-slate-300 disabled:text-slate-500"
          >
            Convert & Map Field
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
